

import { useState, useEffect } from "react"
import TypewriterRendererPlain from "../TypeWriterResponsePlain"

const PracticeDisplay = ({ problems, subject, topic, difficulty, useTypewriter = true }) => {
  const [visibleHints, setVisibleHints] = useState({})
  const [visibleSolutions, setVisibleSolutions] = useState({})

  // Log the received problems for debugging
  useEffect(() => {
    console.log("PracticeDisplay received problems:", problems)

    if (!problems || !Array.isArray(problems)) {
      console.error("Invalid problems data:", problems)
    } else if (problems.length === 0) {
      console.warn("Empty problems array received")
    } else {
      console.log("First problem structure:", problems[0])
    }
  }, [problems])

  // Simple toggle functions using React state
  const toggleHint = (index) => {
    console.log(`Toggling hint for problem index ${index}`)
    setVisibleHints((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  const toggleSolution = (index) => {
    console.log(`Toggling solution for problem index ${index}`)
    setVisibleSolutions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  // Error handling for invalid data
  if (!problems || !Array.isArray(problems) || problems.length === 0) {
    return (
      <div className="p-4 border border-red-300 bg-red-50 rounded-md">
        <h3 className="text-red-600 font-medium">Error: Invalid practice problems data</h3>
        <p className="text-gray-700">
          Unable to display practice problems due to missing or invalid data. Please try generating the problems again.
        </p>
        <pre className="mt-4 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-40">
          {JSON.stringify(problems, null, 2) || "No data received"}
        </pre>
      </div>
    )
  }

  return (
    <div className="practice-problems">
      <h2 className="text-xl font-bold mb-4">
        {topic || subject} Practice Problems ({difficulty} Level)
      </h2>

      {problems.map((problem, index) => (
        <div
          key={`problem-${index}`}
          className="practice-problem mb-6 p-4 border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-all duration-200"
        >
          <h3 className="text-lg font-semibold mb-3 text-gray-800">Problem {index + 1}</h3>

          <div className="problem-question mb-4 p-3 bg-gray-50 rounded-md text-gray-800 math-content">

              <TypewriterRendererPlain content={problem.question} useTypewriter={useTypewriter} />

          </div>

          <div className="problem-controls flex flex-wrap gap-2 mb-4">
            <button
              className="hint-btn px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors font-medium text-sm flex items-center"
              onClick={() => toggleHint(index)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              {visibleHints[index] ? "Hide Hint" : "Show Hint"}
            </button>

            <button
              className="solution-btn px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors font-medium text-sm flex items-center"
              onClick={() => toggleSolution(index)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {visibleSolutions[index] ? "Hide Solution" : "Show Solution"}
            </button>
          </div>

          {visibleHints[index] && (
            <div className="problem-hint mb-4 p-4 bg-gray-100 rounded-md border-l-4 border-gray-800">
              <p className="font-medium text-gray-800 mb-2">Hint:</p>
              <div className="text-gray-700 math-content">

                  <TypewriterRendererPlain content={problem.hint} useTypewriter={useTypewriter} />

              </div>
            </div>
          )}

          {visibleSolutions[index] && (
            <div className="problem-solution p-4 bg-gray-100 rounded-md border-l-4 border-gray-800">
              <p className="font-medium text-gray-800 mb-2">Answer:</p>
              <div className="mb-3 text-gray-700 math-content">
                {useTypewriter ? (
                  <TypewriterRendererPlain content={problem.answer} useTypewriter={useTypewriter} />
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: problem.answer }} />
                )}
              </div>
              <p className="font-medium text-gray-800 mb-2">Explanation:</p>
              <div className="text-gray-700 math-content">
                  <TypewriterRendererPlain content={problem.explanation} useTypewriter={useTypewriter} />

              </div>
            </div>
          )}
        </div>
      ))}

      <p className="text-sm text-gray-500 mt-4">
        To generate more practice problems, use the Practice Generator in the sidebar.
      </p>
    </div>
  )
}

export default PracticeDisplay
